import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';

type Mode = 'login' | 'signup' | 'reset';

export function AuthPage() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setMessage(''); setLoading(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
        navigate('/dashboard');
      } else if (mode === 'signup') {
        await signUp(email, password, displayName);
        navigate('/dashboard');
      } else {
        await resetPassword(email);
        setMessage('Password reset email sent. Check your inbox.');
      }
    } catch (err: any) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(''); setLoading(true);
    try {
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (err: any) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .auth-root {
          min-height: 100vh;
          display: flex;
          background: var(--baize-deep, #0d1f1a);
          overflow: hidden;
          position: relative;
        }

        /* ── felt texture overlay ── */
        .auth-root::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image:
            radial-gradient(ellipse 80% 60% at 20% 10%, rgba(201,168,76,0.06) 0%, transparent 60%),
            radial-gradient(ellipse 60% 80% at 80% 90%, rgba(26,74,58,0.4) 0%, transparent 60%);
          pointer-events: none;
          z-index: 0;
        }

        /* ── decorative snooker ball ── */
        .auth-root::after {
          content: '';
          position: fixed;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          border: 1px solid rgba(201,168,76,0.06);
          top: -200px;
          right: -200px;
          pointer-events: none;
          z-index: 0;
          box-shadow:
            0 0 0 80px rgba(201,168,76,0.02),
            0 0 0 160px rgba(201,168,76,0.015),
            0 0 0 240px rgba(201,168,76,0.01);
        }

        .auth-left {
          display: none;
          flex: 1;
          flex-direction: column;
          justify-content: center;
          padding: 64px;
          position: relative;
          z-index: 1;
        }

        @media (min-width: 900px) {
          .auth-left { display: flex; }
        }

        .auth-left__eyebrow {
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--gold, #c9a84c);
          margin-bottom: 20px;
          opacity: 0.8;
        }

        .auth-left__headline {
          font-family: var(--font-display, 'Georgia', serif);
          font-size: clamp(2.4rem, 4vw, 3.2rem);
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.03em;
          color: var(--chalk, #f5f0e8);
          margin-bottom: 20px;
        }

        .auth-left__headline em {
          font-style: normal;
          color: var(--gold, #c9a84c);
        }

        .auth-left__sub {
          font-size: 0.9rem;
          line-height: 1.7;
          color: var(--chalk-dim, rgba(245,240,232,0.55));
          max-width: 360px;
          margin-bottom: 48px;
        }

        .auth-left__stats {
          display: flex;
          gap: 32px;
        }

        .auth-stat__val {
          font-family: var(--font-display, 'Georgia', serif);
          font-size: 1.6rem;
          font-weight: 800;
          color: var(--gold, #c9a84c);
          letter-spacing: -0.02em;
          line-height: 1;
        }

        .auth-stat__label {
          font-size: 0.6rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--chalk-faint, rgba(245,240,232,0.3));
          margin-top: 4px;
        }

        .auth-divider-v {
          display: none;
          width: 1px;
          background: rgba(245,240,232,0.06);
          margin: 40px 0;
          position: relative;
          z-index: 1;
        }

        @media (min-width: 900px) {
          .auth-divider-v { display: block; }
        }

        .auth-right {
          flex: 0 0 auto;
          width: 100%;
          max-width: 460px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 40px 32px;
          position: relative;
          z-index: 1;
        }

        @media (min-width: 900px) {
          .auth-right {
            padding: 64px 56px;
          }
        }

        .auth-logo {
          margin-bottom: 40px;
        }

        .auth-logo__mark {
          font-family: var(--font-display, 'Georgia', serif);
          font-size: 1.6rem;
          font-weight: 800;
          color: var(--gold, #c9a84c);
          letter-spacing: -0.03em;
          line-height: 1;
        }

        .auth-logo__mark span {
          color: var(--chalk, #f5f0e8);
          font-weight: 300;
        }

        .auth-logo__sub {
          font-size: 0.58rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--chalk-faint, rgba(245,240,232,0.3));
          margin-top: 5px;
        }

        .auth-title {
          font-family: var(--font-display, 'Georgia', serif);
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--chalk, #f5f0e8);
          letter-spacing: -0.02em;
          margin-bottom: 4px;
        }

        .auth-subtitle {
          font-size: 0.8rem;
          color: var(--chalk-dim, rgba(245,240,232,0.55));
          margin-bottom: 28px;
          line-height: 1.5;
        }

        .auth-alert {
          padding: 10px 14px;
          border-radius: 6px;
          font-size: 0.78rem;
          margin-bottom: 16px;
          line-height: 1.4;
        }

        .auth-alert--error {
          background: rgba(231,76,60,0.1);
          border: 1px solid rgba(231,76,60,0.22);
          color: #f87171;
        }

        .auth-alert--success {
          background: rgba(46,204,113,0.1);
          border: 1px solid rgba(46,204,113,0.22);
          color: #4ade80;
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 20px;
        }

        .auth-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .auth-label {
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--chalk-faint, rgba(245,240,232,0.3));
        }

        .auth-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(245,240,232,0.1);
          border-radius: 6px;
          padding: 10px 14px;
          font-size: 0.88rem;
          color: var(--chalk, #f5f0e8);
          outline: none;
          transition: border-color 0.15s, background 0.15s;
          font-family: var(--font-body, inherit);
          width: 100%;
          box-sizing: border-box;
        }

        .auth-input::placeholder {
          color: rgba(245,240,232,0.2);
        }

        .auth-input:focus {
          border-color: rgba(201,168,76,0.5);
          background: rgba(201,168,76,0.04);
        }

        .auth-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 4px 0;
        }

        .auth-divider__line {
          flex: 1;
          height: 1px;
          background: rgba(245,240,232,0.08);
        }

        .auth-divider__text {
          font-size: 0.6rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: var(--chalk-faint, rgba(245,240,232,0.3));
        }

        .auth-google-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 10px 16px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(245,240,232,0.12);
          border-radius: 6px;
          color: var(--chalk-dim, rgba(245,240,232,0.55));
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
          font-family: var(--font-body, inherit);
          margin-top: 4px;
        }

        .auth-google-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.07);
          border-color: rgba(245,240,232,0.2);
          color: var(--chalk, #f5f0e8);
        }

        .auth-google-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .auth-footer {
          margin-top: 22px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .auth-link-btn {
          background: none;
          border: none;
          color: var(--chalk-faint, rgba(245,240,232,0.3));
          font-size: 0.75rem;
          cursor: pointer;
          padding: 0;
          text-align: left;
          font-family: var(--font-body, inherit);
          transition: color 0.15s;
          line-height: 1.5;
        }

        .auth-link-btn:hover {
          color: var(--gold, #c9a84c);
        }
      `}</style>

      <div className="auth-root">
        {/* ── Left panel (desktop only) ── */}
        <div className="auth-left">
          <div className="auth-left__eyebrow">Performance Analytics</div>
          <h1 className="auth-left__headline">
            Track every<br />
            shot. Reach<br />
            <em>your peak.</em>
          </h1>
          <p className="auth-left__sub">
            147coach gives snooker players the data-driven tools pros use —
            pot rates, break averages, safety success, and tier progression,
            all from your phone at the table.
          </p>
          <div className="auth-left__stats">
            <div>
              <div className="auth-stat__val">14</div>
              <div className="auth-stat__label">Day free trial</div>
            </div>
            <div>
              <div className="auth-stat__val">€6.50</div>
              <div className="auth-stat__label">Per month</div>
            </div>
            <div>
              <div className="auth-stat__val">5</div>
              <div className="auth-stat__label">Tier levels</div>
            </div>
          </div>
        </div>

        <div className="auth-divider-v" />

        {/* ── Right panel (form) ── */}
        <div className="auth-right">
          <div className="auth-logo">
            <div className="auth-logo__mark">147<span>coach</span></div>
            <div className="auth-logo__sub">Snooker Performance Analytics</div>
          </div>

          <h2 className="auth-title">
            {mode === 'login' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Reset password'}
          </h2>
          <p className="auth-subtitle">
            {mode === 'login' && 'Welcome back. Track your improvement.'}
            {mode === 'signup' && '14 days free, then €6.50/month.'}
            {mode === 'reset' && 'Enter your email to receive a reset link.'}
          </p>

          {error   && <div className="auth-alert auth-alert--error">{error}</div>}
          {message && <div className="auth-alert auth-alert--success">{message}</div>}

          <form className="auth-form" onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <div className="auth-field">
                <label className="auth-label">Your name</label>
                <input
                  className="auth-input"
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="Jamie Thornton"
                  required
                />
              </div>
            )}
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input
                className="auth-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            {mode !== 'reset' && (
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input
                  className="auth-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            )}
            <Button type="submit" fullWidth loading={loading} size="lg" style={{ marginTop: 4 }}>
              {mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Start Free Trial' : 'Send Reset Email'}
            </Button>
          </form>

          {mode !== 'reset' && (
            <>
              <div className="auth-divider">
                <div className="auth-divider__line" />
                <span className="auth-divider__text">OR</span>
                <div className="auth-divider__line" />
              </div>
              <button
                className="auth-google-btn"
                onClick={handleGoogle}
                disabled={loading}
                type="button"
              >
                <GoogleIcon />
                Continue with Google
              </button>
            </>
          )}

          <div className="auth-footer">
            {mode === 'login' && <>
              <button className="auth-link-btn" onClick={() => setMode('reset')}>Forgot password?</button>
              <button className="auth-link-btn" onClick={() => setMode('signup')}>No account? Start free trial →</button>
            </>}
            {mode === 'signup' && (
              <button className="auth-link-btn" onClick={() => setMode('login')}>Already have an account? Sign in</button>
            )}
            {mode === 'reset' && (
              <button className="auth-link-btn" onClick={() => setMode('login')}>← Back to sign in</button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function friendlyError(code: string): string {
  const map: Record<string, string> = {
    'auth/user-not-found':       'No account found with that email.',
    'auth/wrong-password':       'Incorrect password.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password':        'Password must be at least 6 characters.',
    'auth/invalid-email':        'Please enter a valid email address.',
    'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
    'auth/too-many-requests':    'Too many attempts. Please try again later.',
    'auth/invalid-credential':   'Email or password is incorrect.',
  };
  return map[code] ?? 'Something went wrong. Please try again.';
}